// Utility functions for class date calculations and validations

export const calculateCycleDates = (startDate, cycleType, numberOfCyclesToShow = 6, expirationDate = null) => {
    const start = new Date(startDate);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const expiry = expirationDate ? new Date(expirationDate) : null;

    // Calculate how many cycles we need to generate to cover from start to expiry
    // We'll generate more than needed, then filter to get the next 6 upcoming ones
    let maxCyclesToGenerate = 1000; // Safety limit

    if (expiry) {
        const timeDiff = expiry - start;
        switch (cycleType) {
            case 0: // Daily
                maxCyclesToGenerate = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1;
                break;
            case 1: // Weekly
                maxCyclesToGenerate = Math.ceil(timeDiff / (1000 * 60 * 60 * 24 * 7)) + 1;
                break;
            case 2: // Monthly
                maxCyclesToGenerate = Math.ceil(timeDiff / (1000 * 60 * 60 * 24 * 30)) + 2;
                break;
            case 3: // Quarterly
                maxCyclesToGenerate = Math.ceil(timeDiff / (1000 * 60 * 60 * 24 * 90)) + 2;
                break;
            case 4: // Yearly
                maxCyclesToGenerate = Math.ceil(timeDiff / (1000 * 60 * 60 * 24 * 365)) + 2;
                break;
            default:
                maxCyclesToGenerate = Math.ceil(timeDiff / (1000 * 60 * 60 * 24 * 7)) + 1;
        }
    }

    // Generate all potential dates from start to expiry
    const allPotentialDates = [];
    for (let i = 0; i < maxCyclesToGenerate; i++) {
        const date = new Date(start);

        switch (cycleType) {
            case 0: // Daily
                date.setDate(start.getDate() + i);
                break;
            case 1: // Weekly
                date.setDate(start.getDate() + (i * 7));
                break;
            case 2: // Monthly
                date.setMonth(start.getMonth() + i);
                break;
            case 3: // Quarterly
                date.setMonth(start.getMonth() + (i * 3));
                break;
            case 4: // Yearly
                date.setFullYear(start.getFullYear() + i);
                break;
            default:
                date.setDate(start.getDate() + (i * 7)); // Default to weekly
        }

        // Only add if it's:
        // 1. On or after the start date
        // 2. Today or in the future (include today's sessions)
        // 3. Before or on expiration date (if it exists)
        const isAfterStart = date >= start;
        const isCurrentOrFuture = date >= todayStart;
        const isBeforeExpiry = !expiry || date <= expiry;

        if (isAfterStart && isCurrentOrFuture && isBeforeExpiry) {
            allPotentialDates.push(date);
        }

        // If we have enough future dates or we've passed the expiry, stop generating
        if (allPotentialDates.length >= numberOfCyclesToShow && isCurrentOrFuture) {
            break;
        }

        // Stop if we've passed the expiry date
        if (expiry && date > expiry) {
            break;
        }
    }

    // Return only the next numberOfCyclesToShow (default 6) upcoming classes
    return allPotentialDates
        .sort((a, b) => a - b)
        .slice(0, numberOfCyclesToShow);
};

export const getNextPaidDate = (userClasses, startDate, cycleType) => {
    if (!userClasses || userClasses.length === 0) return null;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const paidBookings = userClasses.filter(b => b.hasPaid);

    // Find the next paid session after today's start (including today)
    const nextPaidBooking = paidBookings
        .map(b => new Date(b.paymentDate))
        .filter(date => date >= todayStart)
        .sort((a, b) => a - b)[0];

    return nextPaidBooking;
};

export const hasAnyPaidBookings = (userClasses) => {
    if (!userClasses) return false;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return userClasses.some(b => b.hasPaid && new Date(b.paymentDate) >= todayStart);
};

export const hasAnyUpcomingPendingBookings = (userClasses) => {
    if (!userClasses) return false;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return userClasses.some(b => !b.hasPaid && new Date(b.paymentDate) >= todayStart);
};

export const getNextClassDate = (startDate, cycleType, expirationDate = null) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dates = calculateCycleDates(startDate, cycleType, 20, expirationDate);
    return dates.find(date => date >= todayStart);
};

export const getCycleTypeName = (cycleType) => {
    switch (cycleType) {
        case 0: return 'Daily';
        case 1: return 'Weekly';
        case 2: return 'Monthly';
        case 3: return 'Quarterly';
        case 4: return 'Yearly';
        default: return 'Weekly';
    }
};
